<template>
  <div class="px-2 md:px-4 py-4">
    <div>
      <!-- Enhanced Page Header -->
      <PageHeader
        show-back-button
        :back-handler="handleBack"
        :title="t('profile.title')"
        :description="t('profile.description')"
        :helper-text="t('profile.helperText')"
        :icon-path="getIconPath('settings')"
      >
        <template #actions>
          <!-- Theme Toggle -->
          <div v-if="false" class="flex items-center space-x-2">
            <HLButton
              id="theme-light-btn"
              @click="setTheme('light')"
              :color="localTheme === 'light' ? 'blue' : 'gray'"
              :variant="localTheme === 'light' ? 'primary' : 'secondary'"
              size="sm"
            >
              <template #iconLeft>
                <Icon name="sun" size="sm" :class="`${THEME_ICON_COLORS.sun}`" />
              </template>
              {{ t('profile.theme.light') }}
            </HLButton>
            
            <HLButton
              id="theme-dark-btn"
              @click="setTheme('dark')"
              :color="localTheme === 'dark' ? 'blue' : 'gray'"
              :variant="localTheme === 'dark' ? 'primary' : 'secondary'"
              size="sm"
            >
              <template #iconLeft>
                <Icon name="moon" size="sm" :class="`${THEME_ICON_COLORS.moon}`" />
              </template>
              {{ t('profile.theme.dark') }}
            </HLButton>
          </div>

          <!-- Change Password Button -->
          <!-- <HLButton
            id="change-password-btn"
            @click="showPasswordModal = true"
            color="gray"
            variant="secondary"
            size="sm"
          >
            <template #iconLeft>
              <Icon name="key" size="sm" />
            </template>
            {{ t('profile.changePassword') }}
          </HLButton> -->
        </template>
      </PageHeader>

      <HLSpace vertical :size="24">
        <!-- Settings Content - Modern Design -->
        <div class="w-full">
        <!-- Profile Information Card -->
        <div :class="[CARD_COLORS.background, CARD_COLORS.border]" class="rounded-2xl overflow-hidden border-4 border-blue-800 dark:border-blue-300">
          
          <form @submit.prevent="updateProfile" class="p-8">
            <div class="space-y-6">
              <!-- Full Name & Role Row -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <HLFormItem 
                  :validation-status="profileErrors.name ? 'error' : 'success'"
                  :feedback="profileErrors.name"
                >
                  <template #label>
                    Full Name <span class="text-red-500">*</span>
                  </template>
                  <HLInput
                    id="name"
                    v-model="profileForm.name"
                    type="text"
                    size="md"
                    required
                    class="w-full"
                  />
                </HLFormItem>

                <HLFormItem>
                  <template #label>
                    <span class="text-gray-300 dark:text-gray-500">Role</span>
                  </template>
                  <HLInput
                    id="role"
                    :model-value="userRole"
                    type="text"
                    size="md"
                    disabled
                    class="w-full bg-gray-100 dark:bg-gray-800 text-gray-300 dark:text-gray-500 cursor-not-allowed border-gray-200 dark:border-gray-700 opacity-50"
                  />
                </HLFormItem>
              </div>

              <!-- Email & Bio Row -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <HLFormItem>
                  <template #label>
                    <span class="text-gray-300 dark:text-gray-500">Email Address</span>
                  </template>
                  <HLInput
                    id="email"
                    :model-value="userProfile?.email"
                    type="email"
                    size="md"
                    disabled
                    class="w-full bg-gray-100 dark:bg-gray-800 text-gray-300 dark:text-gray-500 cursor-not-allowed border-gray-200 dark:border-gray-700 opacity-50"
                  />
                </HLFormItem>

                <HLFormItem>
                  <template #label>
                    Bio
                  </template>
                  <HLInput
                    id="bio"
                    v-model="profileForm.bio"
                    type="text"
                    size="md"
                    :placeholder="t('profile.fields.bioPlaceholder')"
                    class="w-full"
                  />
                </HLFormItem>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-end mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <HLButton
                type="submit"
                color="blue"
                variant="primary"
                size="md"
                :loading="profileLoading"
                :disabled="profileLoading"
                class="min-w-[140px]"
                @click="updateProfile"
              >
                <template #iconLeft>
                  <Icon name="check" size="sm" />
                </template>
                {{ profileLoading ? t('profile.buttons.saving') : t('profile.buttons.saveChanges') }}
              </HLButton>
            </div>
          </form>
        </div>
      </div>
      </HLSpace>

      <!-- Change Password Modal -->
      <HLModal
        id="change-password-modal"
        :show="showPasswordModal"
        :show-header="true"
        :show-footer="true"
        :show-close="true"
        :show-header-icon="false"
        :mask-closable="true"
        :close-on-esc="true"
        width="410"
        @update:show="(value) => !value && closePasswordModal()"
      >
        <template #header>
          <div class="flex items-center gap-3">
            <Icon name="key" size="sm" />
            <span style="font-size: 1.125rem; font-weight: 700; color: #1f2937;">
              Change Password
            </span>
          </div>
        </template>
        
        <template #default>
          <form @submit.prevent="changePassword" class="space-y-4">
            <!-- Current Password -->
            <HLFormItem 
              :validation-status="passwordErrors.currentPassword ? 'error' : 'success'"
              :feedback="passwordErrors.currentPassword"
            >
              <template #label>
                {{ t('profile.fields.currentPassword') }}
                <span class="text-red-500">*</span>
              </template>
              <HLInput
                id="currentPassword"
                v-model="passwordForm.currentPassword"
                type="password"
                size="md"
                :placeholder="t('profile.placeholders.currentPassword')"
                required
              />
            </HLFormItem>

            <!-- New Password -->
            <HLFormItem 
              :validation-status="passwordErrors.newPassword ? 'error' : 'success'"
              :feedback="passwordErrors.newPassword"
            >
              <template #label>
                {{ t('profile.fields.newPassword') }}
                <span class="text-red-500">*</span>
              </template>
              <HLInput
                id="newPassword"
                v-model="passwordForm.newPassword"
                type="password"
                size="md"
                :placeholder="t('profile.placeholders.newPassword')"
                required
              />
            </HLFormItem>

            <!-- Confirm New Password -->
            <HLFormItem 
              :validation-status="passwordErrors.confirmNewPassword ? 'error' : 'success'"
              :feedback="passwordErrors.confirmNewPassword"
            >
              <template #label>
                {{ t('profile.fields.confirmPassword') }}
                <span class="text-red-500">*</span>
              </template>
              <HLInput
                id="confirmPassword"
                v-model="passwordForm.confirmNewPassword"
                type="password"
                size="md"
                :placeholder="t('profile.placeholders.confirmPassword')"
                required
              />
            </HLFormItem>
          </form>
        </template>
        
        <template #footer>
          <div class="flex justify-end gap-4 pt-2 pb-4 pr-4">
            <HLButton
              variant="secondary"
              size="md"
              class="px-6 py-2"
              @click="closePasswordModal"
            >
              {{ t('profile.buttons.cancel') }}
            </HLButton>
            <HLButton
              color="blue"
              variant="primary"
              size="md"
              class="px-6 py-2"
              :loading="passwordLoading"
              @click="changePassword"
            >
              {{ passwordLoading ? t('profile.buttons.updating') : t('profile.buttons.updatePassword') }}
            </HLButton>
          </div>
        </template>
      </HLModal>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useToast } from 'vue-toastification'
import PageHeader from '@/components/ui/PageHeader.vue'
import { HLButton, HLSpace, HLInput, HLCard, HLText, HLIcon, HLContentWrap, HLModal, HLFormItem } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'
import { USER_ROLE_LABELS } from '@/constants/common'
import { t } from '@/i18n/en-us'
import { getIconPath } from '@/constants/icons'
import { CARD_COLORS, THEME_ICON_COLORS } from '@/constants/uiColors'

// Router
const router = useRouter()

// Stores
const authStore = useAuthStore()
const themeStore = useThemeStore()
const toast = useToast()

// Reactive data
const profileLoading = ref(false)
const passwordLoading = ref(false)
const showPasswordModal = ref(false)

const profileForm = reactive({
  name: '',
  bio: ''
})

const profileErrors = reactive({
  name: ''
})

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: ''
})

const passwordErrors = reactive({
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: ''
})

// Computed
const userProfile = computed(() => authStore.userProfile)
const userRole = computed(() => {
  const role = authStore.userRole
  return USER_ROLE_LABELS[role] || role
})
const localTheme = computed(() => themeStore.isDark ? 'dark' : 'light')

const userInitials = computed(() => {
  if (!userProfile.value?.name) return 'U'
  return userProfile.value.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

// Methods
/**
 * Handle back navigation
 * Goes to previous page if history exists, otherwise to dashboard
 */
const handleBack = () => {
  // Check if there's a previous page in history (not the first page)
  if (window.history.length > 1) {
    router.go(-1) // Go back to previous page
  } else {
    router.push('/dashboard') // Default to dashboard if no history
  }
}

const setTheme = (theme) => {
  themeStore.setTheme(theme)
}

const resetPasswordForm = () => {
  passwordForm.currentPassword = ''
  passwordForm.newPassword = ''
  passwordForm.confirmNewPassword = ''
  passwordErrors.currentPassword = ''
  passwordErrors.newPassword = ''
  passwordErrors.confirmNewPassword = ''
}

const closePasswordModal = () => {
  showPasswordModal.value = false
  resetPasswordForm()
}

const updateProfile = async () => {
  // Validate form
  if (!profileForm.name.trim()) {
    profileErrors.name = t('profile.errors.nameRequired')
    return
  }
  
  profileLoading.value = true
  
  try {
    await authStore.updateUserProfile({
      name: profileForm.name.trim(),
      bio: profileForm.bio.trim()
    })
    toast.success(t('profile.success.profileUpdated'))
  } catch (error) {
    toast.error(t('profile.failure.profileUpdate'))
  } finally {
    profileLoading.value = false
  }
}

const changePassword = async () => {
  // Clear previous errors
  passwordErrors.currentPassword = ''
  passwordErrors.newPassword = ''
  passwordErrors.confirmNewPassword = ''
  
  // Validate form
  if (!passwordForm.currentPassword) {
    passwordErrors.currentPassword = t('profile.errors.currentPasswordRequired')
    return
  }
  
  if (!passwordForm.newPassword) {
    passwordErrors.newPassword = t('profile.errors.newPasswordRequired')
    return
  }
  
  if (passwordForm.newPassword.length < 6) {
    passwordErrors.newPassword = t('profile.errors.passwordMinLength')
    return
  }
  
  if (passwordForm.currentPassword === passwordForm.newPassword) {
    passwordErrors.newPassword = t('profile.errors.passwordSameAsCurrent')
    return
  }
  
  if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
    passwordErrors.confirmNewPassword = t('profile.errors.passwordsDoNotMatch')
    return
  }
  
  passwordLoading.value = true
  
  try {
    await authStore.changePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    })
    
    // Show success notification with message
    toast.success('Password changed successfully. Please login with new Password', {
      timeout: 3000
    })
    
    closePasswordModal()
    
    // Logout user after password change and redirect to login
    setTimeout(async () => {
      await authStore.logout()
    }, 3000) // Wait 3 seconds to show success message
    
  } catch (error) {
    const errorMessage = error.message || 
                         error.response?.data?.error || 
                         error.response?.data?.message || 
                         error.details?.error ||
                         t('profile.failure.passwordChange')
    
    const errorCode = error.code || error.response?.data?.code
    
    if (errorCode === 'INVALID_CURRENT_PASSWORD' ||
        errorMessage.toLowerCase().includes('current password') || 
        errorMessage.toLowerCase().includes('incorrect') ||
        errorMessage.toLowerCase().includes('invalid password')) {
      passwordErrors.currentPassword = t('profile.errors.currentPasswordIncorrect')
    } else if (errorCode === 'WEAK_PASSWORD' || errorMessage.toLowerCase().includes('at least 6')) {
      passwordErrors.newPassword = errorMessage
    } else {
      toast.error(errorMessage)
    }
  } finally {
    passwordLoading.value = false
  }
}

// Watchers
watch(userProfile, (newProfile) => {
  if (newProfile) {
    profileForm.name = newProfile.name || ''
    profileForm.bio = newProfile.bio || ''
  }
}, { immediate: true })

// Lifecycle
onMounted(() => {
  if (userProfile.value) {
    profileForm.name = userProfile.value.name || ''
    profileForm.bio = userProfile.value.bio || ''
  }
})
</script>
